import { graphql, readInlineData } from 'react-relay';
import { trackAbTestV2Variant } from 'dibs-buyer-layout/exports/clientABTestV2.ts';
import { filterFalsy } from 'dibs-ts-utils/exports/filterFalsy.ts';
import {
    ITEM_PRICE_IN_SELLABILITY,
    URGENCY_SIGNALS_MW,
    SHIPPING_COST_SB,
    CONTENT_VISIBILITY_SB,
    SHIPS_FROM_SB_TILE,
    REMOVE_COUNTS,
} from '../../utils/abTestHelper.ts';
import { pageTypeConstants } from '../../constants/pageTypeConstants.ts';
import { SortMap } from '../../constants/sbConstants.ts';
import { trackEvent, eventNameConstants, stepInteractionConstants } from 'dibs-tracking';

import { type SbSharedTrackingFunctions_itemSearch$key } from './__generated__/SbSharedTrackingFunctions_itemSearch.graphql.ts';

const itemSearchFragment = graphql`
    fragment SbSharedTrackingFunctions_itemSearch on ItemSearchQueryConnection @inline {
        trackedAbTests
        sponsored {
            maxAvailable
        }
    }
`;

export const trackTotalSponsoredItems = (
    itemSearchRef: SbSharedTrackingFunctions_itemSearch$key,
    page: number
): void => {
    if (!(page === 0 || page === 1)) {
        return;
    }
    const itemSearch = readInlineData(itemSearchFragment, itemSearchRef);
    trackEvent({
        eventName: eventNameConstants.EVENT_RESULT_INTERACTION,
        interaction_type: 'sponsored listings',
        step_interaction_name: stepInteractionConstants.STEP_INTERACTION_DISPLAY,
        step_type: itemSearch.sponsored?.maxAvailable ?? 0,
    });
};

export const trackActiveAbTests = (
    itemSearchRef: SbSharedTrackingFunctions_itemSearch$key,
    testTrackingPredicates?: Record<string, () => boolean | null | undefined>
): void => {
    const itemSearch = readInlineData(itemSearchFragment, itemSearchRef);
    const trackedAbTests = (itemSearch.trackedAbTests || []).filter(filterFalsy);
    for (const abTest of trackedAbTests) {
        const testTrackingPredicate = testTrackingPredicates?.[abTest];
        if (!testTrackingPredicate || testTrackingPredicate()) {
            trackAbTestV2Variant(abTest);
        }
    }
};

export const itemPriceInSellabilityTracking = ({
    pageType,
    sortValue,
}: {
    pageType?: string | null;
    sortValue?: string | null;
}): void => {
    if (
        (pageType === pageTypeConstants.SEARCH && sortValue === SortMap.relevance) ||
        sortValue === SortMap.recommended
    ) {
        trackAbTestV2Variant(ITEM_PRICE_IN_SELLABILITY);
    }
};

export const lowerPriceResultsTracking = ({
    pageType,
    sortValue,
}: {
    pageType?: string | null;
    sortValue?: string | null;
}): void => {
    // https://1stdibs.atlassian.net/browse/PRICING-175
    // https://1stdibs.atlassian.net/browse/PRICING-241
    if (
        pageType &&
        pageType !== pageTypeConstants.BUY &&
        (sortValue === SortMap.recommended || sortValue === SortMap.relevance)
    ) {
        trackAbTestV2Variant('lower-prices-results-pages');
    }
};

export const trackUrgencySignals = (): void => trackAbTestV2Variant(URGENCY_SIGNALS_MW);

export const trackShippingCostSb = (): void => trackAbTestV2Variant(SHIPPING_COST_SB);

export const trackContentVisibilitySb = (): void => trackAbTestV2Variant(CONTENT_VISIBILITY_SB);

export const trackShipsFromSBTile = (): void => trackAbTestV2Variant(SHIPS_FROM_SB_TILE);

export const trackRemoveCounts = (): void => trackAbTestV2Variant(REMOVE_COUNTS);
